import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRightLeft, CircleAlert, PackageCheck, RefreshCw, Search, X } from "lucide-react";
import {
  alternatesSearchFromUrlParams,
  filterAlternatesSkuGroups,
  parseAlternatesSearchQuery,
  stationFilterValue,
} from "@/pages/manufacturing/alternatesSearch";

import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { ApiError, apiFetch } from "@/lib/api";
import { playPairFailureSound, playPairSuccessSound } from "@/lib/floorStationPairAudio";
import { formatKitQuantity } from "@/lib/formatKitQuantity";
import {
  compareReplaceableSerialSwapOrder,
  compareSwapOutStationGroupHeadings,
  swapOutCanonStationProgressionKey,
  swapOutStationHeadingLabel,
} from "@/lib/manufacturingStationFlowRank";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";
import {
  computePointAnchoredContextMenu,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";
import { HoverHintContent, HoverHintWithGlyph, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { hubModalOverlayClassName, hubModalOverlayProps } from "@/components/ui/HubModalOverlay";

import { CreateAlternateOrderModal } from "@/pages/manufacturing/CreateAlternateOrderModal";
import { OpenAlternateOrdersModal } from "@/pages/manufacturing/OpenAlternateOrdersPanel";

const MANUFACTURING_HUB_READ_ONLY = "Your role has read-only access to the Manufacturing hub module.";

/** Custom drag MIME so drop zones can accept drags without relying on React drag state alone. */
const ALTERNATES_DRAG_MIME = "application/x-manufacturing-alternate-serial-fci-ids";

const ALTERNATES_PAGE_HELP =
  "Devices marked as alternates in public.serial_fci, grouped by finished SKU with SGS batch items where they can be added or swapped before pack out. Control-click or Command-click alternates in the same SKU card to select several, then drag onto open slots to add them together. Shift-click selects a range in station-flow order. Drag the same selection onto swap candidates to swap multiple units; in the dialog you must pick the same number of batch devices to swap out. Press Escape to clear the alternate selection.";

const ADD_FROM_INVENTORY_MODAL_HELP =
  "Scan a device on a batch line (including CM shipments already marked shipped to SGS). The hub archives the existing serial_fci episode on that CM batch line (archived_at, rebuild) and creates a new inventory alternate row at Inventory (SGS) with the scanned label. Scan the next unit without closing the modal. Pack-out and shipped SGS units are blocked.";

const REGISTER_TOUCH_SAMPLE_MODAL_HELP =
  "Scan the device label serial or the PCBA serial. The hub looks the unit up the same way Device Lookup does and uses whatever identity is already on file. Missing clamp serial or MCU UUID is fine. If the serial is not in the hub yet, a skeletal identity is created from that one scan. Production stock requires confirmation because it is downgraded. Touch samples cannot be configured for normal orders and use a Final QC path without shipment assignment.";



interface AlternateSerial {
  id: number;
  finishedSku: string | null;
  firmwareVersion: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  deviceLabelSerial: string | null;
  boxSerial: string | null;
  dateAssembled: string | null;
  note: string | null;
  stationId: number | null;
  stationName: string | null;
  alternateEligibility: "alternate" | "qc_repair";
}

function alternateSerialChipTitle(device: AlternateSerial): string {
  return [
    `serial_fci.id ${device.id}`,
    device.pcbaSerial ? `PCBA ${device.pcbaSerial}` : null,
    device.clampSerial ? `Clamp ${device.clampSerial}` : null,
    device.stationName ? `Station ${device.stationName}` : null,
  ]
    .filter(Boolean)
    .join(" • ");
}

interface ReplaceableSerial {
  id?: number | string | null;
  batchItemId?: number | string | null;
  batchItemSku?: string | null;
  finishedSku?: string | null;
  deviceLabelSerial?: string | null;
  boxSerial?: string | null;
  stationName?: string | null;
}

interface SwapModalStationBucket {
  progressionKey: string;
  headingLabel: string;
  devices: ReplaceableSerial[];
}

function buildSwapModalStationBuckets(replaceables: ReplaceableSerial[]): SwapModalStationBucket[] {
  if (replaceables.length === 0) return [];
  const sorted = [...replaceables];
  sorted.sort(compareReplaceableSerialSwapOrder);
  const accumulation = new Map<string, SwapModalStationBucket>();

  for (const device of sorted) {
    const progressionKey = swapOutCanonStationProgressionKey(device.stationName);
    const bucket = accumulation.get(progressionKey);
    if (bucket !== undefined) {
      bucket.devices.push(device);
    } else {
      accumulation.set(progressionKey, {
        progressionKey,
        headingLabel: swapOutStationHeadingLabel(device.stationName),
        devices: [device],
      });
    }
  }

  const rows = [...accumulation.values()].sort((left, right) => compareSwapOutStationGroupHeadings(left, right));
  return rows;
}

function replaceableSerialPillTitle(device: ReplaceableSerial): string {
  return [
    device.id != null ? `serial_fci.id ${String(device.id)}` : null,
    device.batchItemSku != null && String(device.batchItemSku).trim() !== ""
      ? `Batch item SKU ${String(device.batchItemSku).trim()}`
      : null,
    device.stationName != null && String(device.stationName).trim() !== ""
      ? `Station ${String(device.stationName).trim()}`
      : null,
  ]
    .filter(Boolean)
    .join(" • ");
}

function SwapModalGroupedSwapCandidates({
  allBuckets,
  visibleBuckets,
  locationFilterKeys,
  onToggleLocationKey,
  onClearLocationFilters,
  selectedSwapOutSerials,
  requiredSwapOutCount,
  onPickSerial,
}: {
  allBuckets: SwapModalStationBucket[];
  visibleBuckets: SwapModalStationBucket[];
  locationFilterKeys: readonly string[];
  onToggleLocationKey: (progressionKey: string) => void;
  onClearLocationFilters: () => void;
  selectedSwapOutSerials: readonly string[];
  requiredSwapOutCount: number;
  onPickSerial: (serialText: string, event: ReactMouseEvent<HTMLButtonElement>) => void;
}) {
  const selectedSet = useMemo(() => new Set(selectedSwapOutSerials), [selectedSwapOutSerials]);
  if (allBuckets.length === 0) {
    return (
      <p className="text-xs text-slate-500">
        No devices on this batch item line are currently eligible to swap out. Try Refresh after changing stations or
        shipments.
      </p>
    );
  }

  const locationFilterActive = locationFilterKeys.length > 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3">
      <div className="flex flex-wrap items-start justify-between gap-2 pb-3">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Filter by device location</div>
        {locationFilterActive ? (
          <Button type="button" variant="ghost" size="sm" className="-my-1 h-8 shrink-0" onClick={onClearLocationFilters}>
            Clear location filters
          </Button>
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2 pb-3">
        {allBuckets.map((bucket) => {
          const checked = locationFilterKeys.includes(bucket.progressionKey);
          const countLabel = formatCount(bucket.devices.length);
          return (
            <label
              key={bucket.progressionKey}
              className={cn(
                "inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                checked ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
              )}
            >
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                checked={checked}
                onChange={() => onToggleLocationKey(bucket.progressionKey)}
              />
              <span>{bucket.headingLabel}</span>
              <span className="tabular-nums text-slate-400">({countLabel})</span>
            </label>
          );
        })}
      </div>

      {!locationFilterActive ? (
        <p className="pb-3 text-[11px] text-slate-500">
          Grouped by manufacturing floor progression (upstream first). Toggle locations above to narrow the list — station
          badges are omitted here because headings show the stage. Select {formatCount(requiredSwapOutCount)} device
          {requiredSwapOutCount === 1 ? "" : "s"} to swap out.
        </p>
      ) : null}

      <div className="max-h-80 space-y-5 overflow-y-auto pr-1">
        {visibleBuckets.length === 0 ? (
          <p className="text-xs text-amber-800">
            No devices match these location filters. Clear location filters or pick additional locations.
          </p>
        ) : (
          visibleBuckets.map((bucket) => (
            <div key={bucket.progressionKey} className="space-y-2">
              <div className="sticky top-0 z-[1] -mx-1 border-b border-slate-200/80 bg-slate-50/95 px-1 pb-1 text-xs font-semibold text-slate-700 backdrop-blur-sm">
                {bucket.headingLabel}{" "}
                <span className="tabular-nums font-normal text-slate-500">({formatCount(bucket.devices.length)})</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {bucket.devices.map((device) => {
                  const serialText = serialLabel(device);
                  const isSelected = selectedSet.has(serialText);
                  return (
                    <HoverHintContent
                      key={String(device.id ?? `${serialText}-${bucket.progressionKey}`)}
                      hoverHint={replaceableSerialPillTitle(device)}
                    >
                      <button
                        type="button"
                        className={cn(
                          "inline-flex max-w-full cursor-pointer items-center rounded-full border px-2.5 py-1 text-left text-xs shadow-sm transition",
                          isSelected
                            ? "border-brand-400 bg-brand-50 text-brand-900 ring-2 ring-brand-200"
                            : "border-slate-200 bg-white text-slate-700 hover:border-brand-200 hover:bg-brand-50/60",
                        )}
                        onClick={(event) => onPickSerial(serialText, event)}
                      >
                        <span className="min-w-0 truncate font-medium text-slate-900">{serialText}</span>
                      </button>
                    </HoverHintContent>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

interface EligibleBatchItem {
  batchItemId: number;
  batchItemSku: string;
  batchId: number;
  mfrShipByDate: string | null;
  manufacturerSalesOrderNumber: string | null;
  locationName: string | null;
  quantity: number;
  /** Serial rows per finished kit from `sku_divisors`; omitted on stale cache defaults to 1. */
  divisor?: number;
  linkedSerialCount: number;
  openSlotCount: number;
  replaceableSerialCount: number;
  replaceableSerials: ReplaceableSerial[];
  matchReason: string;
}

interface AlternateSkuGroup {
  sku: string;
  comparisonKey: string;
  deviceCount: number;
  devices: AlternateSerial[];
  eligibleBatchItems: EligibleBatchItem[];
}

interface ManufacturingAlternatesResponse {
  locationId: number;
  generatedAt: string;
  alternateDeviceCount: number;
  eligibleBatchItemCount: number;
  openAlternateOrderRemainingCount: number;
  groups: AlternateSkuGroup[];
}

interface MoveAlternatePayload {
  serialFciId: number;
  targetBatchItemId: number;
  mode: "add" | "swap";
  replacementSerial?: string;
}

interface MoveAlternateResponse {
  ok: true;
  serialFciId: number;
  targetBatchItemId: number;
  targetBatchId: number;
  mode: "add" | "swap";
  shipmentId: number | null;
  replacedSerialFciId: number | null;
  warning: string | null;
  message: string;
}

interface PendingSwap {
  devices: AlternateSerial[];
  batchItem: EligibleBatchItem;
}

interface RemovableDevice {
  id: number;
  finishedSku: string | null;
  deviceLabelSerial: string | null;
  boxSerial: string | null;
  pcbaSerial: string | null;
  clampSerial: string | null;
  batchItemId: number;
  batchItemSku: string;
  batchId: number;
  manufacturerSalesOrderNumber: string | null;
  stationName: string | null;
}

interface RemovableDevicesResponse {
  devices: RemovableDevice[];
}

interface RemoveFromBatchPayload {
  serialFciId: number;
}

interface RemoveFromBatchResponse {
  ok: true;
  serialFciId: number;
  batchId: number;
  batchItemId: number;
  message: string;
}

interface AddAlternateFromInventoryPayload {
  scannedSerial: string;
}

interface AddAlternateFromInventoryResponse {
  ok: true;
  archivedSerialFciId: number;
  supersededSerialFciId: number;
  newSerialFciId: number;
  archivedDeviceLabelSerial?: string;
  message: string;
}

interface RegisterTouchSamplePayload {
  scannedSerial: string;
  confirmGoodDeviceDowngrade?: boolean;
}

interface RegisterTouchSampleResponse {
  ok: true;
  serialFciId: number;
  deviceLabelSerial: string;
  message: string;
}

interface MoveAlternateMutationContext {
  previousAlternates?: ManufacturingAlternatesResponse;
}

/** Multi-select is scoped to one SKU group so drops stay compatible with that group's eligible batch items. */
interface AlternateSelectionInGroup {
  groupKey: string;
  ids: number[];
  /** Pivot for shift+click range selection (plain or control-click updates this). */
  rangeAnchorId: number;
}

function formatDate(value: string | null | undefined): string {
  if (value == null || value === "") return "Not scheduled";
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDateTime(value: string | null | undefined): string {
  if (value == null || value === "") return "Unknown";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function formatCount(value: number): string {
  return Math.trunc(value).toLocaleString();
}

/**
 * Parse-based keys from the server look like `t:<template id>|<sorted name=value pairs>`.
 * For the alternates list, show the same concise style as legacy keys: the group finished SKU.
 * The full key remains available on hover for support.
 */
function alternateComparisonKeyDisplay(group: AlternateSkuGroup): { text: string; fullKeyTitle?: string } {
  const key = group.comparisonKey.trim();
  const sku = group.sku.trim();
  if (key === "") {
    return { text: sku || "No SKU" };
  }
  if (/^t:\d+\|/.test(key)) {
    return { text: sku || key, fullKeyTitle: key };
  }
  return { text: key || sku || "No SKU" };
}

function batchItemDivisor(batchItem: EligibleBatchItem): number {
  const raw = batchItem.divisor;
  const numberValue = raw == null ? 1 : Math.trunc(raw);
  return numberValue >= 1 ? numberValue : 1;
}

function serialSlotCapacity(batchItem: EligibleBatchItem): number {
  return batchItem.quantity * batchItemDivisor(batchItem);
}

/** Kit-equivalent progress with two decimal places for partial kits (e.g. 0.66 when 2 serials at divisor 3). */
function formatKitsProgressDisplay(linkedSerialCount: number, divisor: number): string {
  if (divisor <= 1) return formatCount(linkedSerialCount);
  return formatKitQuantity(linkedSerialCount / divisor);
}

function formatAssignmentLine(batchItem: EligibleBatchItem): string {
  const divisor = batchItemDivisor(batchItem);
  if (divisor <= 1) {
    return `${formatCount(batchItem.linkedSerialCount)} of ${formatCount(batchItem.quantity)} assigned`;
  }
  const kits = formatKitsProgressDisplay(batchItem.linkedSerialCount, divisor);
  const serialCap = serialSlotCapacity(batchItem);
  return `${kits} of ${formatCount(batchItem.quantity)} kits assigned (${formatCount(batchItem.linkedSerialCount)} of ${formatCount(serialCap)} serial numbers)`;
}

function serialLabel(device: AlternateSerial | ReplaceableSerial | RemovableDevice): string {
  const preferred = device.deviceLabelSerial ?? device.boxSerial ?? device.id;
  return preferred == null || String(preferred).trim() === "" ? "Unknown serial" : String(preferred);
}

const ALTERNATE_DEVICE_CONTEXT_MENU_WIDTH_PX = 200;

function alternateDeviceLookupSerial(device: AlternateSerial): string | null {
  const label = device.deviceLabelSerial?.trim() ?? "";
  if (label !== "") return label;
  const box = device.boxSerial?.trim() ?? "";
  return box !== "" ? box : null;
}

/** Matches POST /alternates/move swap resolution (label, box serial, or serial_fci id). */
function replaceableSerialMatchesTypedReplacement(device: ReplaceableSerial, replacementRaw: string): boolean {
  const needle = replacementRaw.trim();
  if (needle === "") return false;
  if (String(device.id ?? "") === needle) return true;
  const label = device.deviceLabelSerial?.trim() ?? "";
  const box = device.boxSerial?.trim() ?? "";
  return label === needle || box === needle;
}

function batchLabel(batchItem: EligibleBatchItem): string {
  return `Batch ${batchItem.batchId}`;
}

function normalizeWhitespace(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

/** Matches alternates move shipment-path policy in `manufacturing-alternates-routes.ts`. */
function alternateUsesBatchShipmentPath(device: {
  stationName: string | null | undefined;
  alternateEligibility: string;
}): boolean {
  const station = normalizeWhitespace(device.stationName ?? "").toLowerCase();
  if (station === "quality control") return true;
  if (station === "inventory" && device.alternateEligibility === "alternate") return true;
  return device.alternateEligibility === "qc_repair";
}

function findAlternateDeviceById(
  data: ManufacturingAlternatesResponse | undefined,
  serialFciId: number,
): AlternateSerial | undefined {
  if (!data) return undefined;
  for (const group of data.groups) {
    const found = group.devices.find((device) => device.id === serialFciId);
    if (found) return found;
  }
  return undefined;
}

function optimisticAlternatesMove(
  current: ManufacturingAlternatesResponse | undefined,
  payload: MoveAlternatePayload,
): ManufacturingAlternatesResponse | undefined {
  if (!current) return current;

  let removedDevice = false;
  const groups = current.groups
    .map((group) => {
      const hadDevice = group.devices.some((device) => device.id === payload.serialFciId);
      const devices = hadDevice ? group.devices.filter((device) => device.id !== payload.serialFciId) : group.devices;
      if (hadDevice) removedDevice = true;

      const eligibleBatchItems = group.eligibleBatchItems
        .map((batchItem) => {
          if (batchItem.batchItemId !== payload.targetBatchItemId) return batchItem;

          const divisor = batchItemDivisor(batchItem);
          const cap = batchItem.quantity * divisor;
          const linkedSerialCount =
            payload.mode === "add" ? batchItem.linkedSerialCount + 1 : batchItem.linkedSerialCount;
          const openSlotCount =
            payload.mode === "add" ? Math.max(0, cap - linkedSerialCount) : batchItem.openSlotCount;
          const replaceableSerials =
            payload.mode === "swap" && payload.replacementSerial
              ? batchItem.replaceableSerials.filter(
                  (device) => !replaceableSerialMatchesTypedReplacement(device, payload.replacementSerial!),
                )
              : batchItem.replaceableSerials;
          const replaceableDelta = batchItem.replaceableSerials.length - replaceableSerials.length;

          return {
            ...batchItem,
            linkedSerialCount,
            openSlotCount,
            replaceableSerials,
            replaceableSerialCount: Math.max(0, batchItem.replaceableSerialCount - replaceableDelta),
          };
        })
        .filter((batchItem) => batchItem.openSlotCount > 0 || batchItem.replaceableSerialCount > 0);

      return {
        ...group,
        deviceCount: devices.length,
        devices,
        eligibleBatchItems,
      };
    })
    .filter((group) => group.devices.length > 0);

  return {
    ...current,
    alternateDeviceCount: removedDevice ? Math.max(0, current.alternateDeviceCount - 1) : current.alternateDeviceCount,
    eligibleBatchItemCount: groups.reduce((sum, group) => sum + group.eligibleBatchItems.length, 0),
    groups,
  };
}

function optimisticAlternatesMoveAdds(
  current: ManufacturingAlternatesResponse | undefined,
  serialFciIds: readonly number[],
  targetBatchItemId: number,
): ManufacturingAlternatesResponse | undefined {
  let draft = current;
  for (const serialFciId of serialFciIds) {
    draft = optimisticAlternatesMove(draft, {
      serialFciId,
      targetBatchItemId,
      mode: "add",
    });
  }
  return draft;
}

function toggleAlternateIdInSelection(ids: readonly number[], id: number): number[] {
  const next = new Set(ids);
  if (next.has(id)) {
    next.delete(id);
  } else {
    next.add(id);
  }
  return [...next].sort((left, right) => left - right);
}

/** Inclusive range between anchor and end in station-flow chip order. */
function rangeSelectAlternateIdsInOrder(
  orderedDevices: readonly AlternateSerial[],
  anchorId: number,
  endDeviceId: number,
): number[] {
  const idxAnchor = orderedDevices.findIndex((entry) => entry.id === anchorId);
  const idxEnd = orderedDevices.findIndex((entry) => entry.id === endDeviceId);
  if (idxAnchor < 0 || idxEnd < 0) {
    return [endDeviceId];
  }
  const low = Math.min(idxAnchor, idxEnd);
  const high = Math.max(idxAnchor, idxEnd);
  return orderedDevices.slice(low, high + 1).map((entry) => entry.id);
}

/** Station-flow order (matches chip display order) for deterministic batch adds. */
function alternateDevicesInStationFlowOrderFromList(
  devices: readonly AlternateSerial[],
  idSet: ReadonlySet<number>,
): AlternateSerial[] {
  return [...devices].sort(compareReplaceableSerialSwapOrder).filter((device) => idSet.has(device.id));
}

function EmptyState() {
  return (
    <Card>
      <CardBody className="py-12 text-center">
        <PackageCheck className="mx-auto h-10 w-10 text-slate-300" aria-hidden />
        <h2 className="mt-3 text-base font-semibold text-slate-900">No alternate devices found</h2>
        <p className="mt-1 text-sm text-slate-500">
          No matching rows in <code className="rounded bg-slate-100 px-1">public.serial_fci</code>: either reason{" "}
          <code className="rounded bg-slate-100 px-1">alternate</code>, or reason{" "}
          <code className="rounded bg-slate-100 px-1">qc</code> with a linked{" "}
          <code className="rounded bg-slate-100 px-1">public.quality_incident</code> where{" "}
          <code className="rounded bg-slate-100 px-1">repair_scrap</code> is repair.
        </p>
      </CardBody>
    </Card>
  );
}

function SummaryCard({
  label,
  value,
  helper,
}: {
  label: string;
  value: string;
  helper: string;
}) {
  return (
    <Card>
      <CardBody>
        <div className="text-sm font-medium text-slate-500">{label}</div>
        <div className="mt-2 text-2xl font-semibold text-slate-950">{value}</div>
        <div className="mt-1 text-xs text-slate-500">{helper}</div>
      </CardBody>
    </Card>
  );
}

function DeviceChips({
  devices,
  selectionInGroup,
  groupKey,
  draggedDeviceIds,
  movePending,
  hubWritesAllowed,
  onSelectAlternateGesture,
  onDragStart,
  onDragEnd,
}: {
  devices: AlternateSerial[];
  selectionInGroup: AlternateSelectionInGroup | null;
  groupKey: string;
  draggedDeviceIds: ReadonlySet<number>;
  movePending: boolean;
  hubWritesAllowed: boolean;
  onSelectAlternateGesture: (device: AlternateSerial, event: ReactMouseEvent<HTMLSpanElement>) => void;
  onDragStart: (devices: AlternateSerial[]) => void;
  onDragEnd: () => void;
}) {
  const selectedIdSet =
    selectionInGroup !== null && selectionInGroup.groupKey === groupKey ? new Set(selectionInGroup.ids) : null;
  const [contextMenu, setContextMenu] = useState<{
    clientX: number;
    clientY: number;
    serial: string;
  } | null>(null);
  const [contextMenuBox, setContextMenuBox] = useState<AnchoredMenuPanelBox | null>(null);
  const contextMenuRef = useRef<HTMLDivElement | null>(null);

  const repositionContextMenu = useCallback(() => {
    if (!contextMenu) {
      setContextMenuBox(null);
      return;
    }
    setContextMenuBox(
      computePointAnchoredContextMenu({
        clientX: contextMenu.clientX,
        clientY: contextMenu.clientY,
        menuWidth: ALTERNATE_DEVICE_CONTEXT_MENU_WIDTH_PX,
        maxHeightCap: Math.min(240, Math.floor(window.innerHeight * 0.5)),
      }),
    );
  }, [contextMenu]);

  useLayoutEffect(() => {
    if (!contextMenu) {
      setContextMenuBox(null);
      return;
    }
    repositionContextMenu();
    const onLayout = (): void => {
      repositionContextMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
    };
  }, [contextMenu, repositionContextMenu]);

  useEffect(() => {
    if (!contextMenu) return;
    const onMouseDown = (event: MouseEvent): void => {
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (contextMenuRef.current?.contains(target)) return;
      setContextMenu(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setContextMenu(null);
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [contextMenu]);

  return (
    <>
      <div className="flex flex-wrap gap-2" role="listbox" aria-label="Alternate devices" aria-multiselectable="true">
        {devices.map((device) => {
          const isSelected = selectedIdSet?.has(device.id) === true;
          const isDragPhantom = draggedDeviceIds.has(device.id);
          const lookupSerial = alternateDeviceLookupSerial(device);
          return (
            <HoverHintContent key={device.id} hoverHint={alternateSerialChipTitle(device)}>
              <span
                role="option"
                aria-selected={isSelected}
                draggable={hubWritesAllowed && !movePending}
                onClick={(event) => {
                  if (!hubWritesAllowed || movePending) return;
                  onSelectAlternateGesture(device, event);
                }}
                onContextMenu={(event) => {
                  if (lookupSerial == null) return;
                  event.preventDefault();
                  event.stopPropagation();
                  setContextMenu({
                    clientX: event.clientX,
                    clientY: event.clientY,
                    serial: lookupSerial,
                  });
                }}
                onDragStart={(event) => {
                event.stopPropagation();
                const dragSet =
                  selectedIdSet != null && selectedIdSet.size > 1 && selectedIdSet.has(device.id)
                    ? selectedIdSet
                    : new Set<number>([device.id]);
                const dragging = alternateDevicesInStationFlowOrderFromList(devices, dragSet);
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData(ALTERNATES_DRAG_MIME, dragging.map((entry) => String(entry.id)).join(","));
                event.dataTransfer.setData("text/plain", dragging.map((entry) => String(entry.id)).join(","));
                onDragStart(dragging);
              }}
              onDragEnd={onDragEnd}
              className={cn(
                "inline-flex cursor-grab select-none items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700 transition active:cursor-grabbing",
                isSelected ? "border-brand-400 bg-brand-50 ring-2 ring-brand-200" : "hover:border-brand-200",
                isDragPhantom ? "border-brand-300 opacity-70" : "",
                !hubWritesAllowed || movePending ? "cursor-not-allowed opacity-60" : "",
              )}
            >
              <span className="font-medium text-slate-900">{serialLabel(device)}</span>
              {device.alternateEligibility === "qc_repair" ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 font-medium text-amber-900">QC repair</span>
              ) : null}
              {device.stationName ? <span className="text-slate-400">at {device.stationName}</span> : null}
              </span>
            </HoverHintContent>
          );
        })}
      </div>

      {contextMenu && contextMenuBox
        ? createPortal(
            <div
              ref={contextMenuRef}
              className="fixed rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg"
              style={{
                left: contextMenuBox.left,
                top: contextMenuBox.top,
                width: contextMenuBox.width,
                maxHeight: contextMenuBox.maxHeight,
                zIndex: FLOATING_POPOVER_Z_INDEX,
              }}
              role="menu"
              aria-label="Alternate device actions"
            >
              <Link
                to={`/manufacturing/device-lookup?serial=${encodeURIComponent(contextMenu.serial)}`}
                className="block w-full px-3 py-2 text-left text-slate-800 hover:bg-slate-50"
                role="menuitem"
                onClick={() => setContextMenu(null)}
              >
                Device lookup
              </Link>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function parseAlternateDragIds(raw: string): number[] {
  return raw
    .split(",")
    .map((entry) => Number.parseInt(entry.trim(), 10))
    .filter((id) => Number.isFinite(id) && id > 0);
}

function resolveAlternateDragDevices(
  event: DragEvent<HTMLDivElement>,
  groupDevices: readonly AlternateSerial[],
  draggedDevices: AlternateSerial[] | null,
): AlternateSerial[] {
  const groupById = new Map(groupDevices.map((device) => [device.id, device]));
  if (draggedDevices != null && draggedDevices.length > 0) {
    const fromState = draggedDevices.filter((device) => groupById.has(device.id));
    if (fromState.length > 0) return fromState;
  }
  const raw =
    event.dataTransfer.getData(ALTERNATES_DRAG_MIME) || event.dataTransfer.getData("text/plain");
  const resolved: AlternateSerial[] = [];
  for (const id of parseAlternateDragIds(raw)) {
    const device = groupById.get(id);
    if (device != null) resolved.push(device);
  }
  return resolved;
}

function alternateDragPayloadActive(
  event: DragEvent<HTMLDivElement>,
  draggedDevices: AlternateSerial[] | null,
): boolean {
  if (draggedDevices != null && draggedDevices.length > 0) return true;
  return event.dataTransfer.types.includes(ALTERNATES_DRAG_MIME);
}

function EligibleBatchItemRow({
  batchItem,
  groupDevices,
  draggedDevices,
  alternateDragActive,
  onMoveAlternates,
  movePending,
  hubWritesAllowed,
}: {
  batchItem: EligibleBatchItem;
  groupDevices: readonly AlternateSerial[];
  draggedDevices: AlternateSerial[] | null;
  alternateDragActive: boolean;
  onMoveAlternates: (devices: AlternateSerial[], batchItem: EligibleBatchItem, mode: "add" | "swap") => void;
  movePending: boolean;
  hubWritesAllowed: boolean;
}) {
  const canAdd = batchItem.openSlotCount > 0;
  const canSwap = batchItem.replaceableSerialCount > 0;
  const examples = batchItem.replaceableSerials.slice(0, 3);
  const dragCount = draggedDevices?.length ?? 0;
  const dragActive = dragCount > 0 || alternateDragActive;
  const canSwapDrop =
    canSwap && dragActive && dragCount > 0 && dragCount <= batchItem.replaceableSerialCount;

  function handleDragOver(event: DragEvent<HTMLDivElement>, enabled: boolean): void {
    if (!hubWritesAllowed || !enabled || movePending) return;
    if (!alternateDragPayloadActive(event, draggedDevices)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  function handleDrop(event: DragEvent<HTMLDivElement>, mode: "add" | "swap", enabled: boolean): void {
    event.preventDefault();
    if (!hubWritesAllowed || !enabled || movePending) return;
    const devices = resolveAlternateDragDevices(event, groupDevices, draggedDevices);
    if (devices.length === 0) return;
    if (mode === "swap" && devices.length > batchItem.replaceableSerialCount) return;
    onMoveAlternates(devices, batchItem, mode);
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-950">{batchLabel(batchItem)}</span>
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
              {batchItem.batchItemSku}
            </span>
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium",
                batchItem.matchReason === "Exact SKU match"
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-amber-50 text-amber-700",
              )}
            >
              {batchItem.matchReason}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
            <span>Ship by {formatDate(batchItem.mfrShipByDate)}</span>
            {batchItem.manufacturerSalesOrderNumber ? (
              <span>Sales order {batchItem.manufacturerSalesOrderNumber}</span>
            ) : null}
            <span>{formatAssignmentLine(batchItem)}</span>
          </div>
          {examples.length > 0 ? (
            <div className="mt-2 text-xs text-slate-500">
              Same-line swap examples:{" "}
              {examples.map((device) => (
                <span key={String(device.id)} className="mr-2 font-medium text-slate-700">
                  {serialLabel(device)}
                </span>
              ))}
              {batchItem.replaceableSerials.length > examples.length ? (
                <span>+{batchItem.replaceableSerials.length - examples.length} more</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="min-w-[18rem] space-y-2">
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div
              className={cn(
                "rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-emerald-800 transition",
                hubWritesAllowed && canAdd && dragActive && !movePending ? "border-emerald-300 ring-2 ring-emerald-100" : "",
                !canAdd ? "opacity-55" : "",
              )}
              onDragOver={(event) => handleDragOver(event, canAdd)}
              onDrop={(event) => handleDrop(event, "add", canAdd)}
            >
              <div className="font-semibold">{canAdd ? formatCount(batchItem.openSlotCount) : "0"}</div>
              <div>open slots</div>
              <div className="mt-1 text-[11px] font-medium">Drop alternate here to add</div>
            </div>
            <div
              className={cn(
                "rounded-md border border-sky-100 bg-sky-50 px-3 py-2 text-sky-800 transition",
                hubWritesAllowed && canSwapDrop && !movePending ? "border-sky-300 ring-2 ring-sky-100" : "",
                canSwap && dragActive && !canSwapDrop && !movePending ? "opacity-55" : "",
                !canSwap ? "opacity-55" : "",
              )}
              onDragOver={(event) => handleDragOver(event, canSwapDrop)}
              onDrop={(event) => handleDrop(event, "swap", canSwapDrop)}
            >
              <div className="font-semibold">{canSwap ? formatCount(batchItem.replaceableSerialCount) : "0"}</div>
              <div>swap candidates</div>
              <div className="mt-1 text-[11px] font-medium">
                {dragActive && dragCount > 1
                  ? `Drop ${formatCount(dragCount)} alternates to swap`
                  : "Drop alternate(s) here to swap"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AlternateGroupCard({
  group,
  selectionInGroup,
  draggingDevices,
  draggedDeviceIds,
  alternateDragActive,
  onMoveAlternates,
  onSelectAlternateGesture,
  onDragStart,
  onDragEnd,
  movePending,
  hubWritesAllowed,
}: {
  group: AlternateSkuGroup;
  selectionInGroup: AlternateSelectionInGroup | null;
  draggingDevices: AlternateSerial[] | null;
  draggedDeviceIds: ReadonlySet<number>;
  alternateDragActive: boolean;
  onMoveAlternates: (devices: AlternateSerial[], batchItem: EligibleBatchItem, mode: "add" | "swap") => void;
  onSelectAlternateGesture: (
    device: AlternateSerial,
    groupKey: string,
    orderedGroupDevices: readonly AlternateSerial[],
    event: ReactMouseEvent<HTMLSpanElement>,
  ) => void;
  onDragStart: (devices: AlternateSerial[]) => void;
  onDragEnd: () => void;
  movePending: boolean;
  hubWritesAllowed: boolean;
}) {
  const comparisonKeyShown = alternateComparisonKeyDisplay(group);
  const alternateDevicesSortedByStationFlow = useMemo(
    (): AlternateSerial[] => [...group.devices].sort(compareReplaceableSerialSwapOrder),
    [group.devices],
  );

  const alternateDevicesSelectedCount =
    selectionInGroup !== null && selectionInGroup.groupKey === group.sku ? selectionInGroup.ids.length : 0;

  return (
    <Card>
      <CardHeader className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <CardTitle className="font-mono">{group.sku}</CardTitle>
            <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
              {formatCount(group.deviceCount)} alternate {group.deviceCount === 1 ? "device" : "devices"}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Comparison key:{" "}
            <span
              className="font-mono text-slate-700"
              title={comparisonKeyShown.fullKeyTitle}
            >
              {comparisonKeyShown.text}
            </span>
          </p>
        </div>
        <div className="text-sm font-medium text-slate-700">
          {formatCount(group.eligibleBatchItems.length)} eligible batch{" "}
          {group.eligibleBatchItems.length === 1 ? "item" : "items"}
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        <div>
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Alternate devices
            {alternateDevicesSelectedCount > 0 ? (
              <span className="font-semibold normal-case tabular-nums text-slate-500">
                {" "}
                ({formatCount(alternateDevicesSelectedCount)} selected)
              </span>
            ) : null}
          </div>
          <DeviceChips
            devices={alternateDevicesSortedByStationFlow}
            selectionInGroup={selectionInGroup}
            groupKey={group.sku}
            draggedDeviceIds={draggedDeviceIds}
            movePending={movePending}
            hubWritesAllowed={hubWritesAllowed}
            onSelectAlternateGesture={(device, event) =>
              onSelectAlternateGesture(device, group.sku, alternateDevicesSortedByStationFlow, event)
            }
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <ArrowRightLeft className="h-3.5 w-3.5" aria-hidden />
            Eligible batch items
          </div>
          {group.eligibleBatchItems.length > 0 ? (
            <div className="space-y-2">
              {group.eligibleBatchItems.map((batchItem) => (
                <EligibleBatchItemRow
                  key={batchItem.batchItemId}
                  batchItem={batchItem}
                  groupDevices={group.devices}
                  draggedDevices={draggingDevices}
                  alternateDragActive={alternateDragActive}
                  hubWritesAllowed={hubWritesAllowed}
                  onMoveAlternates={onMoveAlternates}
                  movePending={movePending}
                />
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
              No current SGS batch items can accept this alternate by open slot or pre-pack-out swap.
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export function AlternatesPage() {
  const queryClient = useQueryClient();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = useMemo(
    () =>
      alternatesSearchFromUrlParams({
        search: searchParams.get("search") ?? "",
        searchBatch: searchParams.get("search_batch") ?? "",
        legacyBatch: searchParams.get("batch") ?? "",
      }),
    [searchParams],
  );
  const [moveWarning, setMoveWarning] = useState<string | null>(null);
  const [draggingDevices, setDraggingDevices] = useState<AlternateSerial[] | null>(null);
  const [alternateDragActive, setAlternateDragActive] = useState(false);
  const [alternateSelection, setAlternateSelection] = useState<AlternateSelectionInGroup | null>(null);
  const [batchAddInFlight, setBatchAddInFlight] = useState(false);
  const [batchAddError, setBatchAddError] = useState<string | null>(null);
  const draggedDeviceIds = useMemo(
    (): ReadonlySet<number> => new Set(draggingDevices?.map((device) => device.id) ?? []),
    [draggingDevices],
  );
  const [alternateSearch, setAlternateSearch] = useState(searchFromUrl);
  const alternateSearchInputRef = useRef<HTMLInputElement>(null);
  const focusAlternateSearchBar = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const input = alternateSearchInputRef.current;
      if (input == null) return;
      input.focus();
      input.select();
    });
  }, []);
  const [selectedStations, setSelectedStations] = useState<string[]>([]);
  const [pendingSwap, setPendingSwap] = useState<PendingSwap | null>(null);
  const [selectedSwapOutSerials, setSelectedSwapOutSerials] = useState<string[]>([]);
  const [replacementSerial, setReplacementSerial] = useState("");
  const [batchSwapInFlight, setBatchSwapInFlight] = useState(false);
  const [swapModalLocationFilterKeys, setSwapModalLocationFilterKeys] = useState<string[]>([]);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryScan, setInventoryScan] = useState("");
  const [inventorySuccessDetail, setInventorySuccessDetail] = useState<string | null>(null);
  const inventoryScanInputRef = useRef<HTMLInputElement>(null);
  const [touchSampleModalOpen, setTouchSampleModalOpen] = useState(false);
  const [touchSampleScan, setTouchSampleScan] = useState("");
  const [touchSampleSuccessDetail, setTouchSampleSuccessDetail] = useState<string | null>(null);
  const [touchSampleConfirmPrompt, setTouchSampleConfirmPrompt] = useState<string | null>(null);
  const touchSampleScanInputRef = useRef<HTMLInputElement>(null);
  const focusInventoryScanInput = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const input = inventoryScanInputRef.current;
      if (input == null) return;
      input.focus();
      input.select();
    });
  }, []);

  const focusTouchSampleScanInput = useCallback((): void => {
    window.requestAnimationFrame(() => {
      const input = touchSampleScanInputRef.current;
      if (input == null) return;
      input.focus();
      input.select();
    });
  }, []);
  const [removeSerialSearch, setRemoveSerialSearch] = useState("");
  const [removeBatchSearch, setRemoveBatchSearch] = useState("");
  const [alternatesLoadErrorDismissed, setAlternatesLoadErrorDismissed] = useState(false);
  const [openAlternateOrdersModalOpen, setOpenAlternateOrdersModalOpen] = useState(false);
  const [createAlternateOrderModalOpen, setCreateAlternateOrderModalOpen] = useState(false);
  const alternatesQuery = useQuery<ManufacturingAlternatesResponse, ApiError>({
    queryKey: ["manufacturing", "alternates"],
    queryFn: () => apiFetch<ManufacturingAlternatesResponse>("/api/manufacturing/alternates"),
    refetchOnWindowFocus: false,
  });
  useEffect(() => {
    setAlternatesLoadErrorDismissed(false);
  }, [alternatesQuery.errorUpdatedAt]);

  useEffect(() => {
    if (!inventoryModalOpen) return;
    focusInventoryScanInput();
  }, [focusInventoryScanInput, inventoryModalOpen]);

  useEffect(() => {
    if (!touchSampleModalOpen) return;
    focusTouchSampleScanInput();
  }, [focusTouchSampleScanInput, touchSampleModalOpen]);

  useEffect(() => {
    const data = alternatesQuery.data;
    setAlternateSelection((current) => {
      if (!current || !data) return current;
      const group = data.groups.find((entry) => entry.sku === current.groupKey);
      if (!group) return null;
      const validIds = current.ids.filter((id) => group.devices.some((device) => device.id === id));
      if (validIds.length === 0) return null;
      const nextAnchor = validIds.includes(current.rangeAnchorId) ? current.rangeAnchorId : validIds[0]!;
      const idsUnchanged =
        validIds.length === current.ids.length && validIds.every((id, index) => id === current.ids[index]);
      if (idsUnchanged && nextAnchor === current.rangeAnchorId) return current;
      return { groupKey: current.groupKey, ids: validIds, rangeAnchorId: nextAnchor };
    });
  }, [alternatesQuery.data]);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setAlternateSelection(null);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setAlternateSearch(searchFromUrl);
  }, [searchFromUrl]);

  function updateAlternateSearch(value: string): void {
    setAlternateSearch(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    next.delete("search_batch");
    next.delete("batch");
    if (trimmed === "") {
      next.delete("search");
    } else {
      next.set("search", value);
    }
    setSearchParams(next, { replace: true });
  }
  const normalizedRemoveSerialSearch = removeSerialSearch.trim();
  const normalizedRemoveBatchSearch = removeBatchSearch.trim();
  const removableDevicesQuery = useQuery<RemovableDevicesResponse, ApiError>({
    queryKey: ["manufacturing", "alternates", "removable", normalizedRemoveSerialSearch, normalizedRemoveBatchSearch],
    queryFn: () =>
      apiFetch<RemovableDevicesResponse>("/api/manufacturing/alternates/removable", {
        query: {
          serial: normalizedRemoveSerialSearch || undefined,
          batch: normalizedRemoveBatchSearch || undefined,
        },
      }),
    enabled: removeModalOpen && (normalizedRemoveSerialSearch !== "" || normalizedRemoveBatchSearch !== ""),
    refetchOnWindowFocus: false,
  });
  const moveAlternateMutation = useMutation<
    MoveAlternateResponse,
    ApiError,
    MoveAlternatePayload,
    MoveAlternateMutationContext
  >({
    mutationFn: (payload) =>
      apiFetch<MoveAlternateResponse>("/api/manufacturing/alternates/move", {
        method: "POST",
        body: payload,
      }),
    onMutate: async (payload) => {
      const previousAlternates = queryClient.getQueryData<ManufacturingAlternatesResponse>([
        "manufacturing",
        "alternates",
      ]);
      const device = findAlternateDeviceById(previousAlternates, payload.serialFciId);
      if (device && alternateUsesBatchShipmentPath(device)) {
        setMoveWarning(
          "This unit uses the batch shipment path: linking it to the batch open shipment.",
        );
      } else {
        setMoveWarning(null);
      }

      await queryClient.cancelQueries({ queryKey: ["manufacturing", "alternates"] });
      queryClient.setQueryData<ManufacturingAlternatesResponse>(["manufacturing", "alternates"], (current) =>
        optimisticAlternatesMove(current, payload),
      );
      setDraggingDevices(null);
      return { previousAlternates };
    },
    onError: (_error, _payload, context) => {
      setMoveWarning(null);
      if (context?.previousAlternates) {
        queryClient.setQueryData(["manufacturing", "alternates"], context.previousAlternates);
      }
    },
    onSuccess: (response) => {
      if (response.shipmentId != null) {
        setMoveWarning(
          `Assigned to shipment ${response.shipmentId}. Send to pack out still follows batch completion or a manual freeze on Production calendar.`,
        );
      } else if (response.warning != null && response.warning.trim() !== "") {
        setMoveWarning(response.warning);
      } else {
        setMoveWarning(null);
      }
      setDraggingDevices(null);
      focusAlternateSearchBar();
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
    },
  });
  const removeFromBatchMutation = useMutation<RemoveFromBatchResponse, ApiError, RemoveFromBatchPayload>({
    mutationFn: (payload) =>
      apiFetch<RemoveFromBatchResponse>("/api/manufacturing/alternates/remove-from-batch", {
        method: "POST",
        body: payload,
      }),
    onSuccess: () => {
      setMoveWarning(null);
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates", "removable"] });
    },
  });
  const addFromInventoryMutation = useMutation<
    AddAlternateFromInventoryResponse,
    ApiError,
    AddAlternateFromInventoryPayload
  >({
    mutationFn: (payload) =>
      apiFetch<AddAlternateFromInventoryResponse>("/api/manufacturing/alternates/add-from-inventory", {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data) => {
      setMoveWarning(null);
      playPairSuccessSound();
      setInventorySuccessDetail(
        `Added alternate serial_fci ${String(data.newSerialFciId)} (archived CM row ${String(data.archivedSerialFciId)}).`,
      );
      setInventoryScan("");
      focusInventoryScanInput();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates", "removable"] });
    },
    onError: () => {
      playPairFailureSound();
      focusInventoryScanInput();
    },
  });

  const registerTouchSampleMutation = useMutation<
    RegisterTouchSampleResponse,
    ApiError,
    RegisterTouchSamplePayload
  >({
    mutationFn: (payload) =>
      apiFetch<RegisterTouchSampleResponse>("/api/manufacturing/alternates/register-touch-sample", {
        method: "POST",
        body: payload,
      }),
    onSuccess: (data) => {
      setMoveWarning(null);
      playPairSuccessSound();
      setTouchSampleConfirmPrompt(null);
      setTouchSampleSuccessDetail(data.message);
      setTouchSampleScan("");
      focusTouchSampleScanInput();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
    },
    onError: (error) => {
      const body =
        error.body != null && typeof error.body === "object"
          ? (error.body as { reason?: string; serial?: string })
          : null;
      if (error.status === 409 && body?.reason === "confirm_good_device_downgrade") {
        setTouchSampleConfirmPrompt(body.serial ?? touchSampleScan.trim());
        return;
      }
      playPairFailureSound();
      focusTouchSampleScanInput();
    },
  });

  const performBatchAdd = useCallback(
    async (devices: AlternateSerial[], batchItem: EligibleBatchItem) => {
      if (!hubWritesAllowed || devices.length === 0) return;
      const anyQc = devices.some((device) => alternateUsesBatchShipmentPath(device));
      if (anyQc) {
        setMoveWarning(
          "At least one unit uses the batch shipment path: linking to the batch open shipment.",
        );
      } else {
        setMoveWarning(null);
      }
      setBatchAddError(null);
      await queryClient.cancelQueries({ queryKey: ["manufacturing", "alternates"] });
      const serialFciIds = devices.map((device) => device.id);
      queryClient.setQueryData<ManufacturingAlternatesResponse>(["manufacturing", "alternates"], (current) =>
        optimisticAlternatesMoveAdds(current, serialFciIds, batchItem.batchItemId),
      );
      setDraggingDevices(null);
      setAlternateSelection(null);
      setBatchAddInFlight(true);
      try {
        for (const device of devices) {
          await apiFetch<MoveAlternateResponse>("/api/manufacturing/alternates/move", {
            method: "POST",
            body: { serialFciId: device.id, targetBatchItemId: batchItem.batchItemId, mode: "add" },
          });
        }
        setMoveWarning(null);
        focusAlternateSearchBar();
      } catch (error) {
        const message = error instanceof ApiError ? error.message : String(error);
        setBatchAddError(message);
      } finally {
        setBatchAddInFlight(false);
        void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
      }
    },
    [focusAlternateSearchBar, hubWritesAllowed, queryClient],
  );

  const groups = alternatesQuery.data?.groups ?? [];
  const parsedAlternateSearch = useMemo(() => parseAlternatesSearchQuery(alternateSearch), [alternateSearch]);
  const stationOptions = useMemo(() => {
    const stationNames = new Set<string>();
    for (const group of groups) {
      for (const device of group.devices) {
        stationNames.add(stationFilterValue(device));
      }
    }
    return Array.from(stationNames).sort((left, right) => left.localeCompare(right));
  }, [groups]);
  const selectedStationSet = useMemo(() => new Set(selectedStations), [selectedStations]);
  const stationFilterActive = selectedStationSet.size > 0;
  const batchScopeActive = parsedAlternateSearch.scope === "batch" && parsedAlternateSearch.term !== "";
  const textFilterActive = parsedAlternateSearch.scope === "all" && parsedAlternateSearch.term !== "";
  const filtersActive = batchScopeActive || textFilterActive || stationFilterActive;
  const visibleGroups = useMemo(
    (): AlternateSkuGroup[] =>
      filterAlternatesSkuGroups({
        groups,
        parsedSearch: parsedAlternateSearch,
        selectedStations,
      }),
    [groups, parsedAlternateSearch, selectedStations],
  );
  const groupsWithEligibleItems = useMemo(
    () => visibleGroups.filter((group) => group.eligibleBatchItems.length > 0).length,
    [visibleGroups],
  );
  const visibleDeviceCount = useMemo(
    () => visibleGroups.reduce((sum, group) => sum + group.devices.length, 0),
    [visibleGroups],
  );

  const swapModalStationBuckets = useMemo(
    (): SwapModalStationBucket[] =>
      pendingSwap === null ? [] : buildSwapModalStationBuckets(pendingSwap.batchItem.replaceableSerials),
    [pendingSwap],
  );

  const swapModalLocationFilterActive = swapModalLocationFilterKeys.length > 0;

  const swapModalVisibleBuckets = useMemo((): SwapModalStationBucket[] => {
    if (!swapModalLocationFilterActive) return swapModalStationBuckets;
    const allowed = new Set(swapModalLocationFilterKeys);
    return swapModalStationBuckets.filter((bucket) => allowed.has(bucket.progressionKey));
  }, [swapModalLocationFilterActive, swapModalLocationFilterKeys, swapModalStationBuckets]);

  const toggleSwapModalLocationFilterKey = useCallback((progressionKey: string): void => {
    setSwapModalLocationFilterKeys((current) =>
      current.includes(progressionKey) ? current.filter((key) => key !== progressionKey) : [...current, progressionKey],
    );
  }, []);

  const clearSwapModalLocationFilters = useCallback((): void => {
    setSwapModalLocationFilterKeys([]);
  }, []);

  function toggleStationFilter(station: string): void {
    setSelectedStations((current) =>
      current.includes(station) ? current.filter((value) => value !== station) : [...current, station],
    );
  }

  function moveAlternate(
    device: AlternateSerial,
    batchItem: EligibleBatchItem,
    mode: "add" | "swap",
    replacementSerialValue?: string,
  ): void {
    if (!hubWritesAllowed) return;
    moveAlternateMutation.mutate({
      serialFciId: device.id,
      targetBatchItemId: batchItem.batchItemId,
      mode,
      replacementSerial: replacementSerialValue,
    });
  }

  function requestSwap(devices: AlternateSerial[], batchItem: EligibleBatchItem): void {
    setPendingSwap({ devices, batchItem });
    setSelectedSwapOutSerials([]);
    setReplacementSerial("");
    setSwapModalLocationFilterKeys([]);
    setMoveWarning(null);
  }

  function handleSwapOutPick(serialText: string, event: ReactMouseEvent<HTMLButtonElement>): void {
    if (!pendingSwap) return;
    const requiredCount = pendingSwap.devices.length;
    setSelectedSwapOutSerials((current) => {
      if (event.ctrlKey || event.metaKey) {
        if (current.includes(serialText)) {
          return current.filter((entry) => entry !== serialText);
        }
        if (current.length >= requiredCount) {
          return current;
        }
        return [...current, serialText];
      }
      if (current.length === 1 && current[0] === serialText) {
        return [];
      }
      return [serialText];
    });
    setReplacementSerial("");
  }

  function handleSelectAlternateGesture(
    device: AlternateSerial,
    groupKey: string,
    orderedGroupDevices: readonly AlternateSerial[],
    event: ReactMouseEvent<HTMLSpanElement>,
  ): void {
    if (
      !hubWritesAllowed ||
      moveAlternateMutation.isPending ||
      batchAddInFlight ||
      batchSwapInFlight ||
      removeFromBatchMutation.isPending ||
      addFromInventoryMutation.isPending
    ) {
      return;
    }
    if (event.shiftKey) {
      event.preventDefault();
      setAlternateSelection((current) => {
        if (!current || current.groupKey !== groupKey) {
          return { groupKey, ids: [device.id], rangeAnchorId: device.id };
        }
        const anchorId = orderedGroupDevices.some((entry) => entry.id === current.rangeAnchorId)
          ? current.rangeAnchorId
          : current.ids[0] ?? device.id;
        const rangeIds = rangeSelectAlternateIdsInOrder(orderedGroupDevices, anchorId, device.id);
        return { groupKey, ids: rangeIds, rangeAnchorId: current.rangeAnchorId };
      });
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      setAlternateSelection((current) => {
        if (!current || current.groupKey !== groupKey) {
          return { groupKey, ids: [device.id], rangeAnchorId: device.id };
        }
        const nextIds = toggleAlternateIdInSelection(current.ids, device.id);
        return nextIds.length === 0 ? null : { groupKey, ids: nextIds, rangeAnchorId: device.id };
      });
      return;
    }
    setAlternateSelection({ groupKey, ids: [device.id], rangeAnchorId: device.id });
  }

  function handleMoveAlternates(
    devices: AlternateSerial[],
    batchItem: EligibleBatchItem,
    mode: "add" | "swap",
  ): void {
    if (!hubWritesAllowed) return;
    if (mode === "swap") {
      if (devices.length === 0) return;
      if (devices.length > batchItem.replaceableSerialCount) {
        setMoveWarning(
          `This batch line has ${formatCount(batchItem.replaceableSerialCount)} swap candidate(s); you dropped ${formatCount(devices.length)} alternates. Select fewer or choose another line.`,
        );
        return;
      }
      requestSwap(devices, batchItem);
      return;
    }
    if (devices.length === 0) return;
    if (devices.length > batchItem.openSlotCount) {
      setMoveWarning(
        `This batch line has ${formatCount(batchItem.openSlotCount)} open slot(s); you dropped ${formatCount(devices.length)} alternates. Select fewer or choose another line.`,
      );
      return;
    }
    if (devices.length === 1) {
      moveAlternate(devices[0]!, batchItem, "add");
      return;
    }
    void performBatchAdd(devices, batchItem);
  }

  function closeSwapModal(): void {
    setPendingSwap(null);
    setSelectedSwapOutSerials([]);
    setReplacementSerial("");
    setSwapModalLocationFilterKeys([]);
  }

  const performBatchSwap = useCallback(
    async (devices: AlternateSerial[], batchItem: EligibleBatchItem, replacementSerials: readonly string[]) => {
      if (!hubWritesAllowed || devices.length === 0 || replacementSerials.length !== devices.length) return;
      const anyQc = devices.some((device) => alternateUsesBatchShipmentPath(device));
      if (anyQc) {
        setMoveWarning(
          "At least one unit uses the batch shipment path: linking to the batch open shipment.",
        );
      } else {
        setMoveWarning(null);
      }
      setBatchAddError(null);
      setDraggingDevices(null);
      setAlternateSelection(null);
      setBatchSwapInFlight(true);
      try {
        for (let index = 0; index < devices.length; index += 1) {
          await apiFetch<MoveAlternateResponse>("/api/manufacturing/alternates/move", {
            method: "POST",
            body: {
              serialFciId: devices[index]!.id,
              targetBatchItemId: batchItem.batchItemId,
              mode: "swap",
              replacementSerial: replacementSerials[index],
            },
          });
        }
        setMoveWarning(null);
        closeSwapModal();
        focusAlternateSearchBar();
      } catch (error) {
        const message = error instanceof ApiError ? error.message : String(error);
        setBatchAddError(message);
      } finally {
        setBatchSwapInFlight(false);
        void queryClient.invalidateQueries({ queryKey: ["manufacturing", "alternates"] });
      }
    },
    [focusAlternateSearchBar, hubWritesAllowed, queryClient],
  );

  function submitSwap(): void {
    if (!pendingSwap) return;
    const requiredCount = pendingSwap.devices.length;
    const replacements =
      selectedSwapOutSerials.length === requiredCount
        ? selectedSwapOutSerials
        : requiredCount === 1 && replacementSerial.trim() !== ""
          ? [replacementSerial.trim()]
          : [];
    if (replacements.length !== requiredCount) return;
    void performBatchSwap(pendingSwap.devices, pendingSwap.batchItem, replacements);
  }

  function closeRemoveModal(): void {
    setRemoveModalOpen(false);
    setRemoveSerialSearch("");
    setRemoveBatchSearch("");
  }

  function openInventoryModal(): void {
    setInventoryModalOpen(true);
    setInventoryScan("");
    setInventorySuccessDetail(null);
    addFromInventoryMutation.reset();
  }

  function closeInventoryModal(): void {
    setInventoryModalOpen(false);
    setInventoryScan("");
    setInventorySuccessDetail(null);
    addFromInventoryMutation.reset();
  }

  function submitAddFromInventory(): void {
    if (!hubWritesAllowed) return;
    const trimmed = inventoryScan.trim();
    if (trimmed === "") return;
    setInventorySuccessDetail(null);
    addFromInventoryMutation.mutate({ scannedSerial: trimmed });
  }

  function openTouchSampleModal(): void {
    setTouchSampleModalOpen(true);
    setTouchSampleScan("");
    setTouchSampleSuccessDetail(null);
    setTouchSampleConfirmPrompt(null);
    registerTouchSampleMutation.reset();
  }

  function closeTouchSampleModal(): void {
    setTouchSampleModalOpen(false);
    setTouchSampleScan("");
    setTouchSampleSuccessDetail(null);
    setTouchSampleConfirmPrompt(null);
    registerTouchSampleMutation.reset();
  }

  function submitRegisterTouchSample(confirmGoodDeviceDowngrade = false): void {
    if (!hubWritesAllowed) return;
    const trimmed = touchSampleScan.trim();
    if (trimmed === "") return;
    setTouchSampleSuccessDetail(null);
    registerTouchSampleMutation.mutate({
      scannedSerial: trimmed,
      confirmGoodDeviceDowngrade: confirmGoodDeviceDowngrade || undefined,
    });
  }

  function removeDeviceFromBatch(device: RemovableDevice): void {
    if (!hubWritesAllowed) return;
    setMoveWarning(null);
    removeFromBatchMutation.mutate({ serialFciId: device.id });
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <PageTitleWithHelp hoverHint={ALTERNATES_PAGE_HELP}>Alternates</PageTitleWithHelp>
        </div>
        <div className="flex flex-wrap gap-2">
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpenAlternateOrdersModalOpen(true)}
              disabled={!hubWritesAllowed}
            >
              Alternate order
            </Button>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button type="button" variant="secondary" onClick={() => setRemoveModalOpen(true)} disabled={!hubWritesAllowed}>
              Remove devices from batch
            </Button>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button type="button" variant="secondary" onClick={openTouchSampleModal} disabled={!hubWritesAllowed}>
              Register touch sample
            </Button>
          </ReadOnlyHoverLayer>
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button type="button" variant="secondary" onClick={openInventoryModal} disabled={!hubWritesAllowed}>
              Add alternate from inventory
            </Button>
          </ReadOnlyHoverLayer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => void alternatesQuery.refetch()}
            isLoading={alternatesQuery.isFetching}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
            Refresh
          </Button>
        </div>
      </header>

      {(alternatesQuery.isError && !alternatesLoadErrorDismissed) ||
      moveAlternateMutation.isError ||
      batchAddError != null ||
      removeFromBatchMutation.isError ||
      moveWarning != null ? (
        <div
          className="pointer-events-none fixed right-4 top-4 z-50 flex max-w-md flex-col gap-2 max-sm:left-4 max-sm:max-w-none"
          aria-live="polite"
        >
          {alternatesQuery.isError && !alternatesLoadErrorDismissed ? (
            <div
              className="pointer-events-auto flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 shadow-lg"
              role="alert"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              <div className="min-w-0">{alternatesQuery.error.message}</div>
              <button
                type="button"
                className="-mr-1 rounded p-1 text-red-800 hover:bg-red-100 hover:text-red-950"
                onClick={() => setAlternatesLoadErrorDismissed(true)}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
          {moveAlternateMutation.isError ? (
            <div
              className="pointer-events-auto flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 shadow-lg"
              role="alert"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              <div className="min-w-0">{moveAlternateMutation.error.message}</div>
              <button
                type="button"
                className="-mr-1 rounded p-1 text-red-800 hover:bg-red-100 hover:text-red-950"
                onClick={() => moveAlternateMutation.reset()}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
          {batchAddError != null ? (
            <div
              className="pointer-events-auto flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 shadow-lg"
              role="alert"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              <div className="min-w-0">{batchAddError}</div>
              <button
                type="button"
                className="-mr-1 rounded p-1 text-red-800 hover:bg-red-100 hover:text-red-950"
                onClick={() => setBatchAddError(null)}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
          {removeFromBatchMutation.isError ? (
            <div
              className="pointer-events-auto flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900 shadow-lg"
              role="alert"
            >
              <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
              <div className="min-w-0">{removeFromBatchMutation.error.message}</div>
              <button
                type="button"
                className="-mr-1 rounded p-1 text-red-800 hover:bg-red-100 hover:text-red-950"
                onClick={() => removeFromBatchMutation.reset()}
                aria-label="Dismiss error"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
          {moveWarning != null ? (
            <div
              className="pointer-events-auto flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-medium text-amber-900 shadow-lg"
              role="alert"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" aria-hidden />
              <div className="min-w-0">{moveWarning}</div>
              <button
                type="button"
                className="-mr-1 rounded p-1 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
                onClick={() => setMoveWarning(null)}
                aria-label="Dismiss warning"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <Card>
        <CardBody className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700" htmlFor="alternate-search">
              Search by serial number or batch
            </label>
            <div className="relative mt-2 max-w-xl">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden
              />
              <Input
                ref={alternateSearchInputRef}
                id="alternate-search"
                type="search"
                value={alternateSearch}
                onChange={(event) => updateAlternateSearch(event.target.value)}
                placeholder="Serial number, batch:2050, @batch 2050, or sales order"
                className="pl-9"
                autoComplete="off"
              />
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-slate-700">Filter by alternate station</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {stationOptions.map((station) => {
                const checked = selectedStationSet.has(station);
                return (
                  <label
                    key={station}
                    className={cn(
                      "inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition",
                      checked
                        ? "border-brand-300 bg-brand-50 text-brand-700"
                        : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      checked={checked}
                      onChange={() => toggleStationFilter(station)}
                    />
                    {station}
                  </label>
                );
              })}
            </div>
            {selectedStations.length > 0 ? (
              <Button type="button" variant="ghost" size="sm" className="mt-2" onClick={() => setSelectedStations([])}>
                Clear station filters
              </Button>
            ) : null}
          </div>

          {filtersActive ? (
            <div className="mt-2 text-xs text-slate-500">
              Showing {formatCount(visibleDeviceCount)} matching alternate{" "}
              {visibleDeviceCount === 1 ? "device" : "devices"}
              {batchScopeActive
                ? ` (batch ${parsedAlternateSearch.term} only)`
                : textFilterActive
                  ? " (serial number or batch search)"
                  : ""}
              .
            </div>
          ) : null}
        </CardBody>
      </Card>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
        <SummaryCard
          label="Alternate devices"
          value={formatCount(filtersActive ? visibleDeviceCount : alternatesQuery.data?.alternateDeviceCount ?? 0)}
          helper={filtersActive ? "Matching alternate devices" : "Rows where reason_not_in_batch is alternate"}
        />
        <SummaryCard
          label="Eligible batch items"
          value={formatCount(alternatesQuery.data?.eligibleBatchItemCount ?? 0)}
          helper="Open slots or swappable batch devices"
        />
        <SummaryCard
          label="SKUs with options"
          value={formatCount(groupsWithEligibleItems)}
          helper={`Last refreshed ${formatDateTime(alternatesQuery.data?.generatedAt)}`}
        />
        <SummaryCard
          label="Remaining to configure"
          value={formatCount(alternatesQuery.data?.openAlternateOrderRemainingCount ?? 0)}
          helper="Open alternate order quantity not yet built"
        />
      </div>

      {alternatesQuery.isLoading ? (
        <Card>
          <CardBody className="py-12 text-center text-sm text-slate-500">Loading alternates…</CardBody>
        </Card>
      ) : visibleGroups.length === 0 && !alternatesQuery.isError ? (
        <EmptyState />
      ) : (
        <div className="space-y-4">
          {visibleGroups.map((group) => (
            <AlternateGroupCard
              key={group.sku}
              group={group}
              selectionInGroup={alternateSelection}
              draggingDevices={draggingDevices}
              draggedDeviceIds={draggedDeviceIds}
              alternateDragActive={alternateDragActive}
              onMoveAlternates={handleMoveAlternates}
              onSelectAlternateGesture={handleSelectAlternateGesture}
              onDragStart={(devices) => {
                setDraggingDevices(devices);
                setAlternateDragActive(true);
              }}
              onDragEnd={() => {
                window.requestAnimationFrame(() => {
                  setDraggingDevices(null);
                  setAlternateDragActive(false);
                });
              }}
              movePending={
                moveAlternateMutation.isPending ||
                batchAddInFlight ||
                batchSwapInFlight ||
                removeFromBatchMutation.isPending ||
                addFromInventoryMutation.isPending ||
                registerTouchSampleMutation.isPending
              }
              hubWritesAllowed={hubWritesAllowed}
            />
          ))}
        </div>
      )}

      {pendingSwap ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="alternate-swap-modal-title"
          onClick={closeSwapModal}
        >
          <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 id="alternate-swap-modal-title" className="text-lg font-semibold text-slate-900">
                {pendingSwap.devices.length === 1 ? "Choose serial to swap out" : "Choose serials to swap out"}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Moving {formatCount(pendingSwap.devices.length)} alternate
                {pendingSwap.devices.length === 1 ? "" : "s"} into {batchLabel(pendingSwap.batchItem)} as{" "}
                <span className="font-mono text-slate-700">{pendingSwap.batchItem.batchItemSku}</span>. Select{" "}
                {formatCount(pendingSwap.devices.length)} batch device
                {pendingSwap.devices.length === 1 ? "" : "s"} to remove. Control-click or Command-click to multi-select.
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {pendingSwap.devices.map((device) => (
                  <span
                    key={device.id}
                    className="inline-flex rounded-full border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-900"
                  >
                    {serialLabel(device)}
                  </span>
                ))}
              </div>
            </div>
            <div className="space-y-4 px-5 py-4">
              <div>
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Devices eligible to swap out on this batch item line
                  </span>
                  <span className="text-xs font-medium text-slate-600">
                    Selected {formatCount(selectedSwapOutSerials.length)} of {formatCount(pendingSwap.devices.length)}
                  </span>
                </div>
                <SwapModalGroupedSwapCandidates
                  allBuckets={swapModalStationBuckets}
                  visibleBuckets={swapModalVisibleBuckets}
                  locationFilterKeys={swapModalLocationFilterKeys}
                  onToggleLocationKey={toggleSwapModalLocationFilterKey}
                  onClearLocationFilters={clearSwapModalLocationFilters}
                  selectedSwapOutSerials={selectedSwapOutSerials}
                  requiredSwapOutCount={pendingSwap.devices.length}
                  onPickSerial={handleSwapOutPick}
                />
              </div>
              {pendingSwap.devices.length === 1 ? (
                <>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="replacement-serial">
                    Serial to remove from the batch
                  </label>
                  <Input
                    id="replacement-serial"
                    value={replacementSerial}
                    onChange={(event) => {
                      const next = event.target.value;
                      setReplacementSerial(next);
                      const trimmed = next.trim();
                      setSelectedSwapOutSerials(trimmed === "" ? [] : [trimmed]);
                    }}
                    placeholder="Device label, box serial, or serial_fci id"
                    autoFocus
                  />
                </>
              ) : null}
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <Button type="button" variant="ghost" onClick={closeSwapModal}>
                Cancel
              </Button>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                <Button
                  type="button"
                  onClick={submitSwap}
                  disabled={
                    !hubWritesAllowed ||
                    (selectedSwapOutSerials.length !== pendingSwap.devices.length &&
                      !(pendingSwap.devices.length === 1 && replacementSerial.trim() !== "")) ||
                    moveAlternateMutation.isPending ||
                    batchSwapInFlight
                  }
                  isLoading={moveAlternateMutation.isPending || batchSwapInFlight}
                >
                  {pendingSwap.devices.length === 1
                    ? "Swap serial"
                    : `Swap ${formatCount(pendingSwap.devices.length)} serials`}
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </div>
      ) : null}

      {removeModalOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="remove-from-batch-modal-title"
          onClick={closeRemoveModal}
        >
          <div
            className="max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-lg bg-white shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 id="remove-from-batch-modal-title" className="text-lg font-semibold text-slate-900">
                Remove devices from batch
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Scan a device serial, or enter a batch id or sales order to list removable devices. Pack out
                and shipped devices are locked.
              </p>
            </div>
            <div className="max-h-[calc(90vh-8rem)] space-y-4 overflow-y-auto px-5 py-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="remove-device-serial-search">
                    Device serial
                  </label>
                  <Input
                    id="remove-device-serial-search"
                    value={removeSerialSearch}
                    onChange={(event) => setRemoveSerialSearch(event.target.value)}
                    placeholder="Device label, box serial, PCBA, clamp, or serial_fci id"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700" htmlFor="remove-batch-search">
                    Batch or sales order
                  </label>
                  <Input
                    id="remove-batch-search"
                    value={removeBatchSearch}
                    onChange={(event) => setRemoveBatchSearch(event.target.value)}
                    placeholder="Batch id or sales order"
                  />
                </div>
              </div>

              {removableDevicesQuery.isError ? (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                  {removableDevicesQuery.error.message}
                </div>
              ) : null}

              {normalizedRemoveSerialSearch === "" && normalizedRemoveBatchSearch === "" ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Enter a serial or batch search to find devices.
                </div>
              ) : removableDevicesQuery.isFetching ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  Searching removable devices…
                </div>
              ) : (removableDevicesQuery.data?.devices.length ?? 0) === 0 ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                  No removable devices found for this search.
                </div>
              ) : (
                <div className="space-y-2">
                  {(removableDevicesQuery.data?.devices ?? []).map((device) => (
                    <div
                      key={device.id}
                      className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 md:flex-row md:items-start md:justify-between"
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold text-slate-950">{serialLabel(device)}</span>
                          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-700">
                            {device.finishedSku ?? "No SKU"}
                          </span>
                          {device.stationName ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                              {device.stationName}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                          <span>Batch {device.batchId}</span>
                          <span>Batch item {device.batchItemId}</span>
                          <span className="font-mono">{device.batchItemSku}</span>
                          {device.manufacturerSalesOrderNumber ? (
                            <span>Sales order {device.manufacturerSalesOrderNumber}</span>
                          ) : null}
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                          {device.pcbaSerial ? <span>PCBA {device.pcbaSerial}</span> : null}
                          {device.clampSerial ? <span>Clamp {device.clampSerial}</span> : null}
                          {device.boxSerial ? <span>Box {device.boxSerial}</span> : null}
                        </div>
                      </div>
                      <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          disabled={!hubWritesAllowed || removeFromBatchMutation.isPending}
                          isLoading={removeFromBatchMutation.isPending}
                          onClick={() => removeDeviceFromBatch(device)}
                        >
                          Make alternate
                        </Button>
                      </ReadOnlyHoverLayer>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-200 px-5 py-4">
              <Button type="button" variant="ghost" onClick={closeRemoveModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {inventoryModalOpen ? (
        <div
          {...hubModalOverlayProps(false)}
          className={cn(hubModalOverlayClassName(false), "items-center justify-center")}
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-from-inventory-modal-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeInventoryModal();
            }
          }}
        >
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 id="add-from-inventory-modal-title" className="text-lg font-semibold text-slate-900">
                  Add alternate from inventory
                </h2>
                <HoverHintWithGlyph hoverHint={ADD_FROM_INVENTORY_MODAL_HELP} size="md" />
              </div>
            </div>
            <div className="space-y-4 px-5 py-4">
              {inventorySuccessDetail != null ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  {inventorySuccessDetail}
                </div>
              ) : null}
              {addFromInventoryMutation.isError ? (
                <div
                  className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900"
                  role="alert"
                >
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
                  <div className="min-w-0">{addFromInventoryMutation.error.message}</div>
                </div>
              ) : null}
              <label className="block text-sm font-medium text-slate-700" htmlFor="inventory-scan-serial">
                Device serial
              </label>
              <Input
                ref={inventoryScanInputRef}
                id="inventory-scan-serial"
                value={inventoryScan}
                onChange={(event) => {
                  setInventoryScan(event.target.value);
                  if (addFromInventoryMutation.isError) {
                    addFromInventoryMutation.reset();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitAddFromInventory();
                  }
                }}
                placeholder="Device label, box serial, PCBA, clamp, or serial_fci id"
                autoFocus
                aria-invalid={addFromInventoryMutation.isError}
                className={cn(
                  addFromInventoryMutation.isError &&
                    "border-red-500 bg-red-50 ring-2 ring-red-200 focus:border-red-500 focus:ring-red-200",
                )}
              />
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <Button type="button" variant="ghost" onClick={closeInventoryModal}>
                Close
              </Button>
              <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                <Button
                  type="button"
                  onClick={submitAddFromInventory}
                  disabled={
                    !hubWritesAllowed ||
                    inventoryScan.trim() === "" ||
                    addFromInventoryMutation.isPending
                  }
                  isLoading={addFromInventoryMutation.isPending}
                >
                  Add to inventory alternates
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </div>
      ) : null}

      {touchSampleModalOpen ? (
        <div
          {...hubModalOverlayProps(false)}
          className={cn(hubModalOverlayClassName(false), "items-center justify-center")}
          role="dialog"
          aria-modal="true"
          aria-labelledby="register-touch-sample-modal-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeTouchSampleModal();
            }
          }}
        >
          <div className="w-full max-w-xl rounded-lg bg-white shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
            <div className="border-b border-slate-200 px-5 py-4">
              <div className="flex flex-wrap items-center gap-1.5">
                <h2 id="register-touch-sample-modal-title" className="text-lg font-semibold text-slate-900">
                  Register touch sample
                </h2>
                <HoverHintWithGlyph hoverHint={REGISTER_TOUCH_SAMPLE_MODAL_HELP} size="md" />
              </div>
            </div>
            <div className="space-y-4 px-5 py-4">
              {touchSampleSuccessDetail != null ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                  {touchSampleSuccessDetail}
                </div>
              ) : null}
              {touchSampleConfirmPrompt != null ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <p className="font-medium">Confirm downgrade</p>
                  <p className="mt-2">
                    This device is noted in the system as a good device. Are you sure you want to downgrade it to a touch
                    sample?
                  </p>
                  <p className="mt-2 font-mono text-xs text-amber-900">{touchSampleConfirmPrompt}</p>
                </div>
              ) : null}
              {registerTouchSampleMutation.isError && touchSampleConfirmPrompt == null ? (
                <div
                  className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-900"
                  role="alert"
                >
                  <CircleAlert className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
                  <div className="min-w-0">{registerTouchSampleMutation.error.message}</div>
                </div>
              ) : null}
              <label className="block text-sm font-medium text-slate-700" htmlFor="touch-sample-scan-serial">
                Device label serial or PCBA serial
              </label>
              <Input
                ref={touchSampleScanInputRef}
                id="touch-sample-scan-serial"
                value={touchSampleScan}
                onChange={(event) => {
                  setTouchSampleScan(event.target.value);
                  setTouchSampleConfirmPrompt(null);
                  if (registerTouchSampleMutation.isError) {
                    registerTouchSampleMutation.reset();
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    submitRegisterTouchSample(false);
                  }
                }}
                placeholder="Device label serial or PCBA serial"
                autoFocus
                aria-invalid={registerTouchSampleMutation.isError && touchSampleConfirmPrompt == null}
                className={cn(
                  registerTouchSampleMutation.isError &&
                    touchSampleConfirmPrompt == null &&
                    "border-red-500 bg-red-50 ring-2 ring-red-200 focus:border-red-500 focus:ring-red-200",
                )}
              />
              <p className="text-xs text-slate-500">
                The hub looks this up the same way Device Lookup does. Clamp serial, MCU UUID, and other identity
                fields are filled from that record when they exist. Missing fields are fine.
              </p>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <Button type="button" variant="ghost" onClick={closeTouchSampleModal}>
                Close
              </Button>
              {touchSampleConfirmPrompt != null ? (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setTouchSampleConfirmPrompt(null)}
                    disabled={registerTouchSampleMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                    <Button
                      type="button"
                      onClick={() => submitRegisterTouchSample(true)}
                      disabled={!hubWritesAllowed || registerTouchSampleMutation.isPending}
                      isLoading={registerTouchSampleMutation.isPending}
                    >
                      Yes, register touch sample
                    </Button>
                  </ReadOnlyHoverLayer>
                </>
              ) : (
                <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
                  <Button
                    type="button"
                    onClick={() => submitRegisterTouchSample(false)}
                    disabled={
                      !hubWritesAllowed ||
                      touchSampleScan.trim() === "" ||
                      registerTouchSampleMutation.isPending
                    }
                    isLoading={registerTouchSampleMutation.isPending}
                  >
                    Register touch sample
                  </Button>
                </ReadOnlyHoverLayer>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <OpenAlternateOrdersModal
        open={openAlternateOrdersModalOpen}
        onClose={() => setOpenAlternateOrdersModalOpen(false)}
        onNewOrder={() => {
          setOpenAlternateOrdersModalOpen(false);
          setCreateAlternateOrderModalOpen(true);
        }}
        hubWritesAllowed={hubWritesAllowed}
        manufacturingHubReadOnlyHint={MANUFACTURING_HUB_READ_ONLY}
      />

      <CreateAlternateOrderModal
        open={createAlternateOrderModalOpen}
        onClose={() => setCreateAlternateOrderModalOpen(false)}
        source="alternates_page"
      />
    </div>
  );
}
